import Box from '@mui/material/Box';
import { alpha, keyframes, useTheme } from '@mui/material/styles';

const auroraShift = keyframes`
  0% { background-position: 0% 40%; }
  50% { background-position: 100% 60%; }
  100% { background-position: 0% 40%; }
`;

const floatOrb = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  33% { transform: translate3d(18px, -22px, 0) scale(1.06); }
  66% { transform: translate3d(-14px, 12px, 0) scale(0.96); }
`;

const floatOrbMobile = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50% { transform: translate3d(8px, -12px, 0) scale(1.04); }
`;

const drift = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0) rotate(0deg); }
  33% { transform: translate3d(12px, -16px, 0) rotate(6deg); }
  66% { transform: translate3d(-10px, -6px, 0) rotate(-5deg); }
`;

const sway = keyframes`
  0%, 100% { transform: translateX(0) rotate(var(--capsule-rotate)); }
  50% { transform: translateX(16px) rotate(calc(var(--capsule-rotate) + 10deg)); }
`;

const rise = keyframes`
  0% { transform: translateY(0) scale(0.8); opacity: 0; }
  15% { opacity: 0.7; }
  100% { transform: translateY(-105vh) scale(1.15); opacity: 0; }
`;

const shimmerBeam = keyframes`
  0% { transform: translateX(-130%) skewX(-18deg); opacity: 0; }
  20% { opacity: 0.55; }
  50% { opacity: 0.25; }
  100% { transform: translateX(130%) skewX(-18deg); opacity: 0; }
`;

const spinSlow = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const sparkle = keyframes`
  0%, 100% { opacity: 0.15; transform: scale(0.7); }
  50% { opacity: 0.9; transform: scale(1.25); }
`;

const ringPulse = keyframes`
  0%, 100% { opacity: 0.18; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(1.04); }
`;

const ORBS = [
  { size: { xs: 200, md: 420 }, top: { xs: '-6%', md: '-8%' }, left: { xs: '-22%', md: '-8%' }, delay: 0, color: '#3ab5b0' },
  { size: { xs: 180, md: 360 }, top: { xs: '48%', md: '42%' }, right: { xs: '-24%', md: '-10%' }, delay: 1.4, color: '#2a938f' },
  { size: { xs: 140, md: 260 }, top: { xs: '68%', md: '62%' }, left: { xs: '-10%', md: '8%' }, delay: 2.2, color: '#1f6e6b' },
  { size: { xs: 120, md: 220 }, top: { xs: '18%', md: '12%' }, left: { xs: '58%', md: '62%' }, delay: 0.7, color: '#4ecdc4' },
];

const CROSSES = [
  { top: '10%', left: '7%', size: { xs: 18, md: 26 }, delay: 0, duration: 10, accent: false, mobile: true },
  { top: '18%', left: '82%', size: { xs: 14, md: 18 }, delay: 1.2, duration: 12, accent: true, mobile: true },
  { top: '64%', left: '12%', size: { xs: 16, md: 22 }, delay: 2.4, duration: 11, accent: false, mobile: true },
  { top: '72%', left: '76%', size: { xs: 15, md: 20 }, delay: 0.6, duration: 13, accent: true, mobile: false },
  { top: '38%', left: '90%', size: { xs: 12, md: 16 }, delay: 3.1, duration: 9, accent: false, mobile: false },
  { top: '48%', left: '4%', size: { xs: 13, md: 17 }, delay: 1.8, duration: 14, accent: true, mobile: false },
];

const CAPSULES = [
  { top: '16%', left: '24%', delay: 0.3, duration: 13, rotate: -32, color: '#3ab5b0', mobile: true },
  { top: '54%', left: '52%', delay: 1.8, duration: 15, rotate: 26, color: '#2a938f', mobile: true },
  { top: '34%', left: '8%', delay: 3.4, duration: 12, rotate: -14, color: '#E8A838', mobile: false },
  { top: '78%', left: '86%', delay: 0.9, duration: 16, rotate: 38, color: '#3ab5b0', mobile: false },
  { top: '28%', left: '70%', delay: 2.6, duration: 14, rotate: -48, color: '#1f6e6b', mobile: false },
];

const SPARKS = [
  { top: '14%', left: '18%', delay: 0 },
  { top: '26%', left: '66%', delay: 1.1 },
  { top: '58%', left: '30%', delay: 2.0 },
  { top: '70%', left: '78%', delay: 0.5 },
  { top: '42%', left: '48%', delay: 1.6 },
  { top: '84%', left: '20%', delay: 2.8 },
  { top: '12%', left: '88%', delay: 0.9 },
  { top: '50%', left: '10%', delay: 2.3 },
];

const BUBBLES = Array.from({ length: 12 }, (_, i) => ({
  left: `${6 + (i * 7.5) % 90}%`,
  delay: i * 0.55,
  duration: 11 + (i % 5) * 2.2,
  size: 3 + (i % 4) * 2,
  mobile: i < 5,
}));

function MedicalCross({ size, color, glow }) {
  const arm = Math.max(3, Math.round(size * 0.28));
  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        filter: glow ? `drop-shadow(0 0 8px ${alpha(color, 0.55)})` : 'none',
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          bgcolor: color,
          borderRadius: 1.5,
        },
        '&::before': {
          width: arm,
          height: size,
          top: 0,
          left: (size - arm) / 2,
        },
        '&::after': {
          width: size,
          height: arm,
          top: (size - arm) / 2,
          left: 0,
        },
      }}
    />
  );
}

function Capsule({ color, rotate, isDark }) {
  return (
    <Box
      sx={{
        '--capsule-rotate': `${rotate}deg`,
        width: { xs: 24, md: 34 },
        height: { xs: 10, md: 14 },
        borderRadius: 999,
        overflow: 'hidden',
        display: 'flex',
        boxShadow: `0 4px 14px ${alpha(color, 0.35)}`,
        border: `1px solid ${alpha('#fff', isDark ? 0.12 : 0.35)}`,
      }}
    >
      <Box sx={{ flex: 1, background: `linear-gradient(180deg, ${alpha('#fff', 0.35)}, ${color})` }} />
      <Box
        sx={{
          flex: 1,
          background: isDark
            ? `linear-gradient(180deg, ${alpha('#fff', 0.35)}, ${alpha('#fff', 0.12)})`
            : 'linear-gradient(180deg, #fff, #f3fbfa)',
        }}
      />
    </Box>
  );
}

export default function PharmacyBackground() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const aurora = isDark
    ? `linear-gradient(125deg, #071414 0%, #0d2423 22%, #123836 48%, #0f2c2a 72%, #0a1a1a 100%)`
    : `linear-gradient(125deg, #d7f1ef 0%, #e8f8f6 24%, #f3fcfb 48%, #dff4f1 74%, #eaf8f6 100%)`;

  const crossColor = isDark ? '#3ab5b0' : '#2a938f';
  const accent = '#E8A838';

  return (
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        minHeight: '100vh',
        zIndex: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        background: aurora,
        backgroundSize: '220% 220%',
        animation: `${auroraShift} 18s ease-in-out infinite`,
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
        transform: 'translateZ(0)',
        '@supports (min-height: 100dvh)': {
          minHeight: '100dvh',
        },
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
          backgroundSize: '100% 100%',
          '& *': { animation: 'none !important' },
        },
      }}
    >
      {/* Color blooms */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: isDark
            ? `radial-gradient(ellipse 70% 45% at 12% 8%, ${alpha('#3ab5b0', 0.32)}, transparent 58%),
               radial-gradient(ellipse 60% 40% at 92% 28%, ${alpha('#2a938f', 0.24)}, transparent 55%),
               radial-gradient(ellipse 65% 42% at 70% 92%, ${alpha('#E8A838', 0.1)}, transparent 58%),
               radial-gradient(ellipse 50% 35% at 20% 78%, ${alpha('#1f6e6b', 0.22)}, transparent 55%)`
            : `radial-gradient(ellipse 70% 45% at 10% 5%, ${alpha('#3ab5b0', 0.34)}, transparent 58%),
               radial-gradient(ellipse 60% 40% at 95% 25%, ${alpha('#2a938f', 0.2)}, transparent 55%),
               radial-gradient(ellipse 65% 42% at 72% 95%, ${alpha('#E8A838', 0.12)}, transparent 58%),
               radial-gradient(ellipse 50% 35% at 18% 80%, ${alpha('#4ecdc4', 0.18)}, transparent 55%)`,
        }}
      />

      {/* Sweeping light beam */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: '35%',
          width: { xs: '28%', md: '22%' },
          background: `linear-gradient(90deg, transparent, ${alpha('#fff', isDark ? 0.06 : 0.35)}, transparent)`,
          animation: `${shimmerBeam} 9s ease-in-out infinite`,
          filter: 'blur(8px)',
        }}
      />

      {/* Floating orbs */}
      {ORBS.map((orb, i) => (
        <Box
          key={`orb-${i}`}
          sx={{
            position: 'absolute',
            width: orb.size,
            height: orb.size,
            borderRadius: '50%',
            top: orb.top,
            left: orb.left,
            right: orb.right,
            background: `radial-gradient(circle at 35% 35%, ${alpha(orb.color, isDark ? 0.38 : 0.32)} 0%, ${alpha(orb.color, 0.08)} 45%, transparent 70%)`,
            animation: {
              xs: `${floatOrbMobile} ${8 + orb.delay}s ease-in-out infinite`,
              md: `${floatOrb} ${7 + orb.delay}s ease-in-out infinite`,
            },
            animationDelay: `${orb.delay}s`,
            filter: { xs: 'blur(10px)', md: 'blur(6px)' },
            willChange: 'transform',
          }}
        />
      ))}

      {/* Desktop decorative ring */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 520,
          height: 520,
          ml: '-260px',
          mt: '-260px',
          borderRadius: '50%',
          border: `1px solid ${alpha(crossColor, 0.14)}`,
          boxShadow: `inset 0 0 60px ${alpha(crossColor, 0.06)}, 0 0 80px ${alpha(crossColor, 0.05)}`,
          animation: `${ringPulse} 8s ease-in-out infinite`,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 28,
            borderRadius: '50%',
            border: `1px dashed ${alpha(crossColor, 0.18)}`,
            animation: `${spinSlow} 48s linear infinite`,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 70,
            borderRadius: '50%',
            border: `1px solid ${alpha(accent, 0.12)}`,
          },
        }}
      />

      {/* Medical crosses — desktop */}
      {CROSSES.map((item, i) => (
        <Box
          key={`cross-d-${i}`}
          sx={{
            position: 'absolute',
            top: item.top,
            left: item.left,
            display: { xs: 'none', md: 'block' },
            animation: `${drift} ${item.duration}s ease-in-out infinite`,
            animationDelay: `${item.delay}s`,
            opacity: 0.55,
            willChange: 'transform',
          }}
        >
          <MedicalCross
            size={item.size.md}
            color={item.accent ? accent : crossColor}
            glow
          />
        </Box>
      ))}

      {/* Medical crosses — mobile */}
      {CROSSES.filter((c) => c.mobile).map((item, i) => (
        <Box
          key={`cross-m-${i}`}
          sx={{
            position: 'absolute',
            top: item.top,
            left: item.left,
            display: { xs: 'block', md: 'none' },
            animation: `${drift} ${item.duration + 2}s ease-in-out infinite`,
            animationDelay: `${item.delay}s`,
            opacity: 0.42,
            willChange: 'transform',
          }}
        >
          <MedicalCross
            size={item.size.xs}
            color={item.accent ? accent : crossColor}
            glow
          />
        </Box>
      ))}

      {/* Capsules */}
      {CAPSULES.map((item, i) => (
        <Box
          key={`capsule-${i}`}
          sx={{
            position: 'absolute',
            top: item.top,
            left: item.left,
            display: { xs: item.mobile ? 'block' : 'none', md: 'block' },
            animation: `${sway} ${item.duration}s ease-in-out infinite`,
            animationDelay: `${item.delay}s`,
            opacity: { xs: 0.55, md: 0.75 },
            willChange: 'transform',
            '--capsule-rotate': `${item.rotate}deg`,
            transform: `rotate(${item.rotate}deg)`,
          }}
        >
          <Capsule color={item.color} rotate={item.rotate} isDark={isDark} />
        </Box>
      ))}

      {/* Rising bubbles */}
      {BUBBLES.map((p, i) => (
        <Box
          key={`bubble-${i}`}
          sx={{
            position: 'absolute',
            bottom: '-4%',
            left: p.left,
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            background: `radial-gradient(circle at 30% 30%, ${alpha('#fff', 0.85)}, ${alpha(crossColor, 0.55)})`,
            boxShadow: `0 0 10px ${alpha(crossColor, 0.35)}`,
            animation: `${rise} ${p.duration}s linear infinite`,
            animationDelay: `${p.delay}s`,
            display: { xs: p.mobile ? 'block' : 'none', md: 'block' },
            willChange: 'transform, opacity',
          }}
        />
      ))}

      {/* Sparkles */}
      {SPARKS.map((s, i) => (
        <Box
          key={`spark-${i}`}
          sx={{
            position: 'absolute',
            top: s.top,
            left: s.left,
            width: { xs: 4, md: 5 },
            height: { xs: 4, md: 5 },
            borderRadius: '50%',
            bgcolor: i % 2 === 0 ? accent : crossColor,
            boxShadow: `0 0 12px ${alpha(i % 2 === 0 ? accent : crossColor, 0.8)}`,
            animation: `${sparkle} ${3.2 + (i % 3)}s ease-in-out infinite`,
            animationDelay: `${s.delay}s`,
            display: { xs: i < 5 ? 'block' : 'none', md: 'block' },
          }}
        />
      ))}

      {/* Soft mesh / diamond lattice — desktop */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'absolute',
          inset: 0,
          opacity: isDark ? 0.07 : 0.06,
          backgroundImage: `
            linear-gradient(${alpha(crossColor, 0.55)} 1px, transparent 1px),
            linear-gradient(90deg, ${alpha(crossColor, 0.55)} 1px, transparent 1px)
          `,
          backgroundSize: '56px 56px',
          maskImage: 'radial-gradient(ellipse at center, black 25%, transparent 78%)',
          transform: 'perspective(800px) rotateX(55deg) translateY(-8%)',
          transformOrigin: 'center top',
        }}
      />

      {/* Mobile dotted texture */}
      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'absolute',
          inset: 0,
          opacity: isDark ? 0.16 : 0.14,
          backgroundImage: `radial-gradient(${alpha(crossColor, 0.5)} 1.1px, transparent 1.1px)`,
          backgroundSize: '20px 20px',
          maskImage: 'radial-gradient(ellipse at center, black 20%, transparent 75%)',
        }}
      />

      {/* Edge vignette for content clarity */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: isDark
            ? `radial-gradient(ellipse at center, transparent 42%, ${alpha('#000', 0.35)} 100%)`
            : `radial-gradient(ellipse at center, transparent 48%, ${alpha('#1f6e6b', 0.07)} 100%)`,
        }}
      />

      {/* Bottom fade for mobile chrome */}
      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: 120,
          background: isDark
            ? `linear-gradient(180deg, transparent, ${alpha('#0a1a1a', 0.75)})`
            : `linear-gradient(180deg, transparent, ${alpha('#eaf8f6', 0.85)})`,
        }}
      />
    </Box>
  );
}
